var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "theme-ui/jsx-runtime";
import React from "react";
import { Flex, Box } from "theme-ui";
import { debounce } from "ts-debounce";
import { Colors, HeadingRegularVariants, ImageVariants, TextVariants } from "../../theme";
import { ColorRadio } from "../ColorRadio";
import { ProductPrice } from "./ProductPrice";
import { Label3 } from "../Text/Label3";
import { ScreenReaderOnly } from "../ScreenReaderOnly";
import { keyframes } from "@emotion/react";
export var ProductCardVariant;
(function (ProductCardVariant) {
    ProductCardVariant["ORIGINAL"] = "ORIGINAL";
    ProductCardVariant["EXP_OCT_22"] = "EXP_OCT_22";
    ProductCardVariant["CAROUSEL"] = "CAROUSEL";
})(ProductCardVariant || (ProductCardVariant = {}));
export const ProductCard = (_a) => {
    var { onImageClick, children, index = 0, imageRatio = ImageVariants.ratio_1x1 } = _a, props = __rest(_a, ["onImageClick", "children", "index", "imageRatio"]);
    const [isHovered, setIsHovered] = React.useState(false);
    const [hasHovered, setHasHovered] = React.useState(false);
    const debouncedHandleMouseLeave = debounce(() => setIsHovered(false), 100);
    const handleOnMouseEnter = () => {
        if (props.selectedColorway.hoverImageUrl) {
            setIsHovered(true);
            setHasHovered(true);
        }
        debouncedHandleMouseLeave.cancel();
    };
    React.useEffect(() => {
        setIsHovered(false);
        setHasHovered(false);
    }, [props.selectedColorway.hoverImageUrl]);
    const fadeIn = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
    const propsIndex = index || 0;
    const listPageVariant = props.productCardVariant === ProductCardVariant.EXP_OCT_22 ||
        props.productCardVariant === ProductCardVariant.CAROUSEL;
    const carouselVariant = props.productCardVariant === ProductCardVariant.CAROUSEL;
    return (_jsxs(Flex, { sx: {
            border: props.border ? "1px solid" : "none",
            borderColor: "grey.3",
            borderRadius: "3px",
            flexDirection: "column",
            gap: listPageVariant ? 16 : undefined,
            pb: listPageVariant ? 16 : undefined,
            overflow: "hidden",
            position: "relative",
            "& a::after": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                right: 0,
                bottom: 0
            }
        }, children: [_jsxs(Box, { sx: {
                    bg: `${Colors.softBackground}`,
                    position: "relative"
                }, children: [props.badge && props.badge.length > 0 && (_jsx(Box, { sx: {
                            position: "absolute",
                            height: 32,
                            width: "auto",
                            padding: 12,
                            bg: "white.0",
                            right: 12,
                            top: 12,
                            display: "flex",
                            alignContent: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "grey.1",
                            borderRadius: 2,
                            zIndex: 2
                        }, children: _jsx(Label3, { sx: { height: "auto", mt: -3, fontWeight: "bold" }, children: props.badge }) })), _jsxs(Box, { sx: { variant: `images.${imageRatio}`, zIndex: 1 }, onMouseOver: () => handleOnMouseEnter(), onMouseLeave: (e) => debouncedHandleMouseLeave(), children: [_jsx(Box, { sx: {
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    opacity: isHovered ? 0 : 1,
                                    transition: "opacity .2s"
                                }, children: props.image({
                                    imageAlt: "",
                                    imageUrl: props.selectedColorway.imageUrl,
                                    productUrl: props.url,
                                    onClick: onImageClick
                                }) }), props.selectedColorway.hoverImageUrl && hasHovered && (_jsx(Box, { sx: {
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    opacity: isHovered ? 1 : 0,
                                    transition: "opacity .25s",
                                    animation: `${fadeIn} .5s`
                                }, children: props.image({
                                    imageAlt: "",
                                    imageUrl: props.selectedColorway.hoverImageUrl,
                                    productUrl: props.url,
                                    onClick: onImageClick
                                }) }))] })] }), _jsx(Flex, { sx: {
                    flexDirection: "column",
                    flexGrow: 1
                }, id: `CardBody-${index}-${props.id}`, children: _jsxs(Flex, { sx: {
                        flex: 1,
                        p: props.border ? 16 : 0,
                        gap: listPageVariant ? 24 : 16,
                        flexDirection: "column"
                    }, onFocus: handleOnMouseEnter, onBlur: () => debouncedHandleMouseLeave(), children: [_jsxs(Flex, { sx: {
                                flexDirection: "column",
                                px: carouselVariant ? 16 : 0
                            }, children: [_jsx(ProductCardTitle, Object.assign({}, props)), _jsx(ProductCardDescription, Object.assign({}, props))] }), _jsxs(Flex, { dir: "row", mt: "auto", sx: {
                                flexWrap: "wrap",
                                gap: 8,
                                px: carouselVariant ? 16 : 0
                            }, children: [_jsx(ProductPrice, { price: props.selectedColorway.price, discountPrice: props.selectedColorway.discountedPrice, style: {
                                        minWidth: "50%"
                                    } }), _jsx(ProductCardColorOptions, Object.assign({ propsIndex: propsIndex }, props))] })] }) })] }, `product-card-${props.id}`));
};
export const ProductCardTitle = (props) => {
    return (_jsx("span", { sx: {
            variant: props.productCardVariant === ProductCardVariant.EXP_OCT_22
                ? `text.${TextVariants.label2Bold}`
                : `text.${HeadingRegularVariants.sixth}`,
            color: Colors.text
        }, children: props.link({ name: props.name, url: props.url }) }));
};
export const ProductCardDescription = (props) => {
    return (_jsx("span", { sx: { variant: `text.${TextVariants.label2}`, color: Colors.muted }, children: props.description }));
};
const ProductCardColorOptions = (props) => {
    var _a;
    return (_jsxs(_Fragment, { children: [_jsx(ScreenReaderOnly, { children: _jsx("span", { id: `color-label-${props.propsIndex}-${props.id}`, children: (_a = props.translations) === null || _a === void 0 ? void 0 : _a.color }) }), _jsx(Flex, { "aria-labelledby": `color-label-${props.propsIndex}-${props.id}`, role: "radiogroup", sx: {
                    flexGrow: 0,
                    flexShrink: 1,
                    justifyContent: "space-evenly",
                    zIndex: 2,
                    gap: props.gap
                }, children: props.colorways.map((colorway, index) => {
                    return (_jsx(ColorRadio, { radioButtonName: `color-radio-${props.propsIndex}-${props.id}`, colorName: colorway.colorName, colorHex: colorway.colorHex, onSelect: () => {
                            if (props.setSelection && colorway.isInStock) {
                                props.setSelection(colorway.colorCode || "");
                            }
                        }, inStock: colorway.isInStock, swatchSize: 20, checked: colorway.colorHex === props.selectedColorway.colorHex }, index));
                }) })] }));
};
//# sourceMappingURL=ProductCard.js.map